import { utcUnixMs } from "@dashboard-config"

const rateLimitMap = new Map<string, { count: number; resetTime: number }>()
const RATE_LIMIT_WINDOW = 60 * 1000
const MAX_REQUESTS = 5
const MAX_RATE_LIMIT_RECORDS = 1000

type ContactProxyHandlerArgs = {
  request: Request
}

type ContactProxyDependencies = {
  mailerApiUrl?: string
  fetchImpl?: typeof fetch
  siteOrigin?: string
  domainOrigin?: string
}

function runtimeEnv(key: string): string | undefined {
  return (
    import.meta.env[key] ||
    (typeof process !== "undefined" ? process.env[key as keyof typeof process.env] : undefined)
  )
}

function allowedOriginSet(dependencies: ContactProxyDependencies = {}): Set<string> {
  const origins = [
    "https://playbrain.com",
    "https://www.playbrain.com",
    "https://dev.playbrain.com",
    "http://localhost:3000",
    "http://127.0.0.1:3000",
    dependencies.siteOrigin ?? runtimeEnv("SITE"),
    dependencies.domainOrigin ?? runtimeEnv("DOMAIN"),
  ].filter((origin): origin is string => typeof origin === "string" && origin.length > 0)
  return new Set(origins)
}

export function allowedCorsOrigin(
  origin: string | null,
  dependencies: ContactProxyDependencies = {}
): string | null {
  if (!origin) return null
  return allowedOriginSet(dependencies).has(origin) ? origin : null
}

function pruneRateLimitMap(now: number) {
  for (const [ip, record] of rateLimitMap) {
    if (now > record.resetTime) rateLimitMap.delete(ip)
  }

  while (rateLimitMap.size > MAX_RATE_LIMIT_RECORDS) {
    const oldestKey = rateLimitMap.keys().next().value
    if (!oldestKey) break
    rateLimitMap.delete(oldestKey)
  }
}

export function resetContactProxyRateLimitState() {
  rateLimitMap.clear()
}

export function enforceRateLimit(ip: string, now = utcUnixMs()): boolean {
  pruneRateLimitMap(now)
  const record = rateLimitMap.get(ip)

  if (!record || now > record.resetTime) {
    rateLimitMap.set(ip, { count: 1, resetTime: now + RATE_LIMIT_WINDOW })
    return true
  }

  if (record.count >= MAX_REQUESTS) return false
  record.count++
  return true
}

function corsHeaders(origin: string | null, dependencies: ContactProxyDependencies) {
  const headers = new Headers({
    "Access-Control-Allow-Methods": "POST, OPTIONS",
    "Access-Control-Allow-Headers": "Content-Type",
  })
  const allowedOrigin = allowedCorsOrigin(origin, dependencies)
  if (allowedOrigin) headers.set("Access-Control-Allow-Origin", allowedOrigin)
  return headers
}

export function createContactProxyHandlers(dependencies: ContactProxyDependencies = {}) {
  const fetchImpl = dependencies.fetchImpl ?? fetch

  return {
    POST: async ({ request }: ContactProxyHandlerArgs) => {
      const hasExplicitMailerEndpoint = Object.hasOwn(dependencies, "mailerApiUrl")
      const mailerApiUrl = hasExplicitMailerEndpoint ? dependencies.mailerApiUrl : runtimeEnv("MAILER_API_URL")
      if (!mailerApiUrl) {
        return Response.json({ error: "Mailer endpoint is not configured" }, { status: 500 })
      }

      const forwardedFor = request.headers.get("x-forwarded-for")
      const clientIp = forwardedFor?.split(",")[0]?.trim() || "unknown"

      const origin = request.headers.get("origin")
      const allowedOrigin = allowedCorsOrigin(origin, dependencies)
      if (origin && !allowedOrigin) {
        return Response.json({ success: false, message: "Origin not allowed" }, { status: 403 })
      }

      if (!enforceRateLimit(clientIp)) {
        return Response.json(
          { success: false, message: "Rate limit exceeded. Please try again later." },
          { status: 429 }
        )
      }

      try {
        const body = await request.text()
        const headers = new Headers({ "Content-Type": "application/json" })
        if (allowedOrigin) headers.set("Origin", allowedOrigin)

        const response = await fetchImpl(mailerApiUrl, {
          method: "POST",
          headers,
          body,
        })
        const data = await response.text()

        return new Response(data, {
          status: response.status,
          headers: new Headers({
            "Content-Type": response.headers.get("Content-Type") || "application/json",
            ...Object.fromEntries(corsHeaders(origin, dependencies)),
          }),
        })
      } catch (error) {
        console.error("Error proxying to mailer:", error)
        return Response.json({ error: "Failed to send email" }, { status: 500 })
      }
    },
    OPTIONS: async ({ request }: ContactProxyHandlerArgs) => {
      const origin = request.headers.get("origin")
      if (origin && !allowedCorsOrigin(origin, dependencies)) return new Response(null, { status: 403 })
      return new Response(null, {
        status: 204,
        headers: corsHeaders(origin, dependencies),
      })
    },
  }
}
